/**
 * BLRRD Pointwise Filters — Posterize
 *
 * Quantize each channel to N levels. Standard photo-editing math:
 *
 *   out = round(in * (N - 1)) / (N - 1)
 *
 * Same formula Adobe Photoshop has shipped since v1.0. NOT a dither
 * — strict nearest-step quantization gives the classic "poster"
 * look (visible bands).
 *
 * Pipeline slot: POST-VIEW-TRANSFORM. Posterize on scene-linear
 * floats produces nonsense (linear steps don't match visual steps).
 * Display-referred posterize lines up with what the user sees on
 * the monitor — that's the entire point of the filter.
 *
 * Identity short-circuit: levels >= 256 is effectively no-op for
 * 8-bit-equivalent precision (steps below visible threshold).
 */

const MIN_LEVELS = 2;
const MAX_LEVELS = 256;

/** Default Posterize params. */
export function defaultPosterizeParams() {
    return { levels: 4, perChannel: true };
}

/** True iff `params` represents a no-op. */
export function isIdentityPosterize(params) {
    if (!params) return true;
    const L = params.levels;
    if (typeof L !== 'number' || !isFinite(L)) return true;
    return L >= MAX_LEVELS;
}

/**
 * Apply Posterize to a Float32 RGBA buffer in place.
 *
 * @param {Float32Array} rgba
 * @param {number}       w
 * @param {number}       h
 * @param {object}       params      { levels, perChannel? }
 * @param {object}       [opts]      { selectionMask? }
 * @returns {Float32Array}           same `rgba` reference
 */
export function applyPosterize(rgba, w, h, params, opts) {
    validateBuffer(rgba, w, h);
    if (isIdentityPosterize(params)) return rgba;
    const mask = opts && opts.selectionMask ? opts.selectionMask : null;
    if (mask && mask.length !== w * h) {
        throw new Error('applyPosterize: selectionMask length mismatch');
    }

    const levels = clampInt(params.levels, MIN_LEVELS, MAX_LEVELS);
    const step = 1 / (levels - 1);
    const N = w * h;
    const perChannel = params.perChannel !== false;

    if (perChannel) {
        for (let p = 0; p < N; p++) {
            const i = p << 2;
            const m = mask ? mask[p] : 1;
            if (m <= 0) continue;
            const r = quantize(rgba[i],     step, levels);
            const g = quantize(rgba[i + 1], step, levels);
            const b = quantize(rgba[i + 2], step, levels);
            if (m >= 1) {
                rgba[i] = r;
                rgba[i + 1] = g;
                rgba[i + 2] = b;
            } else {
                const inv = 1 - m;
                rgba[i]     = inv * rgba[i]     + m * r;
                rgba[i + 1] = inv * rgba[i + 1] + m * g;
                rgba[i + 2] = inv * rgba[i + 2] + m * b;
            }
        }
    } else {
        /* Luminance-driven posterize — same level chosen for R/G/B. */
        for (let p = 0; p < N; p++) {
            const i = p << 2;
            const m = mask ? mask[p] : 1;
            if (m <= 0) continue;
            const Y = 0.2126 * rgba[i] + 0.7152 * rgba[i + 1] + 0.0722 * rgba[i + 2];
            const Yq = quantize(Y, step, levels);
            const k = Y > 1e-7 ? (Yq / Y) : 1;
            const r = rgba[i]     * k;
            const g = rgba[i + 1] * k;
            const b = rgba[i + 2] * k;
            if (m >= 1) {
                rgba[i] = r;
                rgba[i + 1] = g;
                rgba[i + 2] = b;
            } else {
                const inv = 1 - m;
                rgba[i]     = inv * rgba[i]     + m * r;
                rgba[i + 1] = inv * rgba[i + 1] + m * g;
                rgba[i + 2] = inv * rgba[i + 2] + m * b;
            }
        }
    }
    return rgba;
}

function quantize(v, step, levels) {
    if (v <= 0) return 0;
    if (v >= 1) return 1;
    return Math.round(v * (levels - 1)) * step;
}

function clampInt(v, lo, hi) {
    let n = Math.round(v);
    if (n < lo) n = lo;
    else if (n > hi) n = hi;
    return n;
}

function validateBuffer(rgba, w, h) {
    if (!(rgba instanceof Float32Array)) {
        throw new TypeError('applyPosterize: rgba must be Float32Array');
    }
    if (rgba.length !== w * h * 4) {
        throw new Error('applyPosterize: rgba.length !== w*h*4');
    }
}
